/* Plans — three-tier pricing page for BeatCredsi™.
 *
 * Tiers:
 *   • VIP Trial    (STARTER)             $0   FOREVER   → Get Started Free
 *   • Passport Pro (MOST POPULAR)        $14  MONTH     → Unlock Passport Pro  (founding)
 *   • RoyAI Elite  (INTELLIGENCE SUITE)  $30  MONTH     → Unlock RoyAI Elite   (royai_elite)
 *
 * Prices use the brand display font (Fraunces / display serif), NOT
 * the LCD monospace font, per the design brief.
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Check, Lock, Crown, Sparkles, UserPlus, Shield, Brain, Building2, Users,
  Loader2, LineChart, GraduationCap, AlertTriangle, DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const PLANS = [
  {
    key: "trial",
    badge: "STARTER",
    name: "VIP TRIAL",
    Icon: UserPlus,
    accent: "#10B981",
    subtitle: "Initial score check and\n60-minute trial access.",
    priceLabel: "$0",
    priceSub: "/ FOREVER",
    included: [
      "Music Creator Score™ (MCS)",
      "Score Breakdown (SBD)",
      "Smart Audit Link (SAL) – 60m Trial",
      "Metadata Audit – Deep Dive Revenue Report (DDRR) – 60m Trial",
    ],
    locked: [
      "BeatCredsi Passport™",
      "RoyAI Suite",
    ],
    ctaLabel: "Get Started Free",
    ctaNote: "No credit card required.",
    action: { kind: "route", to: "/signup?next=/dashboard" },
    ctaVariant: "outline",
  },
  {
    key: "passport_pro",
    badge: "VERIFICATION",
    ribbon: "MOST POPULAR",
    name: "PASSPORT PRO",
    Icon: Crown,
    accent: "#D4AF37",
    subtitle: "For active producers building\ncredit authority & placements.",
    priceLabel: "$14",
    priceSub: "/ MONTH",
    priceAnnual: "$120",
    priceAnnualSub: "/ YEAR",
    savingsAnnual: "Save $48 · 29% off",
    sectionLabel: "PASSPORT PRO FEATURES",
    included: [
      "All Starter Features",
      "BeatCredsi Passport™",
      "Achievement Vault",
      "BeatCredsi Academy™ Roadmap",
      "Gaps & Next Moves",
    ],
    locked: [
      "Smart Audit Link (RoyAI Suite)",
      "RoyAI Suite",
    ],
    ctaLabel: "Unlock Passport Pro",
    ctaNote: "Cancel Anytime",
    action: {
      kind: "stripe",
      monthly: "https://buy.stripe.com/bJedR8eOPay8eCZcIEbEA02",
      annual:  "https://buy.stripe.com/eVq9AS8qr0XygL7aAwbEA07",
    },
    ctaVariant: "primary-gold",
  },
  {
    key: "royai_elite",
    badge: "INTELLIGENCE SUITE",
    name: "RoyAI Elite",
    Icon: Sparkles,
    accent: "#A78BFA",
    subtitle: "Full suite for executives, catalog owners\n& heavy producers.",
    priceLabel: "$30",
    priceSub: "/ MONTH",
    priceAnnual: "$200",
    priceAnnualSub: "/ YEAR",
    savingsAnnual: "Save $160 · 44% off",
    sectionLabel: "INCLUDES PASSPORT PRO + ALL ROYAI SUITE FEATURES",
    included: [
      "Smart Audit Link (SAL)",
      "Metadata Audit – Deep Dive Revenue Report (DDRR)",
      "Catalog Summary (CS)",
      "Royalty & UGC Opportunity Analyzer™ (RUOA)",
      "Royalty Hunter™ (RH)",
      "Verified by Evidence (VBE) system",
      "Verified Portfolio",
    ],
    ctaLabel: "Unlock RoyAI Elite",
    ctaNote: "Cancel Anytime",
    action: {
      kind: "stripe",
      monthly: "https://buy.stripe.com/5kQ4gygWXbCc7axfUQbEA09",
      annual:  "https://buy.stripe.com/9B6cN4bCDgWwamJcIEbEA05",
    },
    ctaVariant: "primary-purple",
  },
];

const VALUE_PROPS = [
  { Icon: Shield,    title: "Secure Verification",       body: "Your data is encrypted\nand protected." },
  { Icon: Brain,     title: "AI Powered Analysis",       body: "Advanced AI scans\nmetadata, credits,\nand revenue." },
  { Icon: Building2, title: "Professional Infrastructure", body: "Built for the music industry\nby music professionals." },
  { Icon: Users,     title: "For Creators, By Creators",  body: "Designed for producers,\nartists & songwriters." },
];

export default function Plans() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [polling, setPolling] = useState(false);
  const [billing, setBilling] = useState("monthly"); // "monthly" | "annual"

  const handleClick = async (plan) => {
    if (plan.action.kind === "route") {
      if (!user) navigate(plan.action.to);
      else navigate("/dashboard");
      return;
    }
    if (plan.action.kind === "stripe") {
      // Direct Stripe Payment Link — no server round-trip needed.
      const url = billing === "annual" ? plan.action.annual : plan.action.monthly;
      if (!url) { toast.error("This plan isn't available for that billing period yet."); return; }
      window.location.href = url;
      return;
    }
    if (plan.action.kind === "checkout") {
      if (!user) { navigate("/signup?next=/plans"); return; }
      setBusy(plan.key);
      try {
        const { data } = await api.post("/payments/checkout", {
          package_id: plan.action.package_id,
          origin_url: window.location.origin,
        });
        if (!data.url) throw new Error("No checkout URL returned");
        window.location.href = data.url;
      } catch (err) {
        toast.error(formatApiError(err) || "Checkout failed");
        setBusy(null);
      }
    }
  };

  // Stripe return handler.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ps = params.get("payment_status");
    const sid = params.get("session_id");
    if (!ps) return;
    if (ps === "cancelled") { toast.info("Payment cancelled."); navigate("/plans", { replace: true }); return; }
    if (ps !== "success" || !sid) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.status === "paid") { toast.success("Membership activated."); navigate("/dashboard", { replace: true }); return; }
        if (data.status === "expired") { toast.error("Payment session expired."); navigate("/plans", { replace: true }); return; }
        if (attempts >= 20) { toast.info("Still finalizing — check your dashboard shortly."); navigate("/dashboard", { replace: true }); return; }
        setTimeout(poll, 1500);
      } catch {
        if (attempts >= 20) { toast.error("Could not confirm payment."); navigate("/plans", { replace: true }); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [location.search, navigate]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-12 lg:py-16">
        {/* HERO */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14 items-start mb-16">
          <div>
            <div className="mb-6">
              <div className="font-display text-3xl sm:text-4xl font-medium">
                BeatCredsi<span className="text-amber-500">™</span>
              </div>
              <div className="text-xs text-neutral-400 mt-1">Music Infrastructure Intelligence</div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-tight tracking-tight mb-4">
              Don&apos;t send a linktree.<br />
              Send your <span className="text-amber-500">Passport.</span>
            </h1>
            <p className="text-neutral-400 leading-relaxed">
              Get scored. Get verified. Get placements.
            </p>
          </div>

          {/* Center — Passport card visual */}
          <div className="flex items-center justify-center">
            <div
              className="relative w-full max-w-sm p-6 border-2 border-amber-500 bg-gradient-to-br from-[#0d0d0d] to-black"
              style={{ boxShadow: "0 30px 80px -30px #D4AF37cc" }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="text-[10px] font-mono-tech uppercase tracking-widest text-amber-500">
                  BEATCRED$I<br />PASSPORT<span className="align-super text-[8px]">™</span>
                </div>
                <div className="w-8 h-8 border border-amber-500 flex items-center justify-center rounded">
                  <span className="font-display font-bold text-amber-500 text-sm">B</span>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-16 h-16 bg-neutral-800 border border-neutral-700 rounded" aria-hidden />
                <div className="flex-1">
                  <div className="font-display text-lg font-medium leading-tight">
                    {user?.name || "Your Name"}
                  </div>
                  <div className="text-[10px] text-neutral-400 mt-0.5">Producer · Composer · Artist</div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-[9px] font-mono-tech text-amber-500 uppercase tracking-widest">MCS™ Score</span>
                  </div>
                  <div className="font-display text-3xl font-bold text-amber-500 leading-none mt-1">
                    862
                  </div>
                  <div className="text-[9px] text-neutral-500 mt-0.5">EXPERT LEVEL</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-y-1 text-[10px]">
                {[
                  ["VERIFIED CREDITS", "26"],
                  ["RELEASES", "12"],
                  ["PROTECTED WORKS", "18"],
                  ["ROYALTY HEALTH", "92%"],
                ].map(([label, val]) => (
                  <div key={label} className="flex items-center justify-between border-b border-white/5 py-1">
                    <span className="text-neutral-400">{label}</span>
                    <span className="text-white font-medium">{val}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="w-10 h-10 bg-white/90 rounded-sm" aria-hidden />
                <div className="text-right text-[9px] text-neutral-500">
                  <div>PASSPORT ID</div>
                  <div className="font-mono-tech text-neutral-300">BC-7F3A-9D21</div>
                  <div className="mt-1">MEMBER SINCE JUN 2025</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-6">
              <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-white">One Passport.</div>
              <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-purple-400 mt-1">
                Unlimited Opportunities.
              </div>
            </div>
            <ul className="space-y-4">
              {[
                { Icon: Users,       body: "Share your verified profile with artists,\nlabels, and industry." },
                { Icon: Shield,      body: "Prove your credit, showcase your catalog,\nand build instant trust." },
                { Icon: Sparkles,    body: "Open doors to placements, sync deals,\nand bigger opportunities." },
                { Icon: LineChart,   body: "Backed by data. Built for business." },
              ].map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-neutral-300 leading-relaxed">
                  <p.Icon size={16} className="mt-1 shrink-0 text-neutral-400" />
                  <span className="whitespace-pre-line">{p.body}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {polling && (
          <div className="mb-8 border border-amber-500/40 bg-amber-500/5 p-4 flex items-center gap-3 justify-center">
            <Loader2 size={16} className="animate-spin text-amber-500" />
            <span className="text-sm text-amber-400 font-mono-tech uppercase tracking-widest">Confirming your payment…</span>
          </div>
        )}

        {/* Billing toggle — Monthly / Annual (annual saves 17%) */}
        <div className="flex items-center justify-center mb-8">
          <div
            className="inline-flex items-center gap-1 p-1 border border-white/10 bg-[#0a0a0a] rounded-full"
            role="group"
            aria-label="Billing period"
            data-testid="plans-billing-toggle"
          >
            {[
              { key: "monthly", label: "Monthly" },
              { key: "annual",  label: "Annual · Save up to 44%" },
            ].map((b) => {
              const active = billing === b.key;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => setBilling(b.key)}
                  data-testid={`plans-billing-${b.key}`}
                  className={`px-5 py-2 text-xs font-mono-tech uppercase tracking-[0.2em] rounded-full transition-colors ${
                    active
                      ? "bg-amber-500 text-black font-semibold"
                      : "text-neutral-400 hover:text-white"
                  }`}
                >
                  {b.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* PLANS GRID */}
        <div
          data-testid="plans-grid"
          className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 mb-16"
        >
          {PLANS.map((p) => {
            const Icon = p.Icon;
            const isBusy = busy === p.key;
            const isMostPopular = !!p.ribbon;
            return (
              <div
                key={p.key}
                data-testid={`plan-${p.key}`}
                className="relative bg-[#0a0a0a] border rounded-lg p-7 lg:p-8 flex flex-col"
                style={{
                  borderColor: `${p.accent}66`,
                  boxShadow: isMostPopular
                    ? `0 30px 80px -30px ${p.accent}cc, inset 0 0 0 1px ${p.accent}44`
                    : `0 20px 60px -30px ${p.accent}88`,
                }}
              >
                {/* MOST POPULAR ribbon */}
                {p.ribbon && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-[10px] font-mono-tech font-semibold uppercase tracking-[0.2em] rounded-full"
                    style={{ background: p.accent, color: "#000" }}
                  >
                    {p.ribbon}
                  </div>
                )}

                {/* Header — Icon + STARTER/VERIFICATION/INTELLIGENCE SUITE badge */}
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 border"
                    style={{
                      borderColor: `${p.accent}88`,
                      background: `${p.accent}14`,
                    }}
                  >
                    <Icon size={26} strokeWidth={1.6} style={{ color: p.accent }} />
                  </div>
                  <span
                    className="text-[9px] font-mono-tech font-semibold uppercase tracking-[0.22em] px-3 py-1 border rounded-full"
                    style={{
                      borderColor: `${p.accent}66`,
                      color: p.accent,
                      background: `${p.accent}0d`,
                    }}
                  >
                    {p.badge}
                  </span>
                </div>

                {/* Name */}
                <h3
                  className="font-display text-3xl leading-tight mb-3 font-medium"
                  style={{ color: "#fff" }}
                >
                  {p.name}
                </h3>

                {/* Subtitle */}
                <p className="text-sm text-neutral-400 leading-snug mb-6 whitespace-pre-line">
                  {p.subtitle}
                </p>

                {/* Price — brand display font */}
                <div className="pb-6 mb-6 border-b border-white/10">
                  <div className="flex items-baseline gap-2">
                    <span
                      className="font-display font-medium text-6xl leading-none"
                      style={{ color: p.accent }}
                    >
                      {billing === "annual" && p.priceAnnual ? p.priceAnnual : p.priceLabel}
                    </span>
                    <span className="text-neutral-400 text-sm font-medium">
                      {billing === "annual" && p.priceAnnualSub ? p.priceAnnualSub : p.priceSub}
                    </span>
                  </div>
                  {billing === "annual" && p.savingsAnnual && (
                    <div
                      className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono-tech font-semibold uppercase tracking-[0.2em]"
                      style={{
                        background: `${p.accent}18`,
                        color: p.accent,
                        border: `1px solid ${p.accent}55`,
                      }}
                      data-testid={`plan-${p.key}-savings`}
                    >
                      {p.savingsAnnual}
                    </div>
                  )}
                </div>

                {/* Section header */}
                {p.sectionLabel && (
                  <div
                    className="text-[10px] font-mono-tech font-semibold uppercase tracking-[0.22em] mb-4"
                    style={{ color: p.accent }}
                  >
                    {p.sectionLabel}
                  </div>
                )}

                {/* Features — included */}
                <ul className="space-y-3 mb-6 flex-1">
                  {p.included.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-neutral-200 leading-snug">
                      <span
                        className="mt-0.5 shrink-0 w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ background: `${p.accent}20`, border: `1px solid ${p.accent}` }}
                      >
                        <Check size={10} strokeWidth={3} style={{ color: p.accent }} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                  {(p.locked || []).map((f, i) => (
                    <li key={`l-${i}`} className="flex items-start gap-3 text-sm text-neutral-500 leading-snug">
                      <span className="mt-0.5 shrink-0 w-4 h-4 flex items-center justify-center">
                        <Lock size={11} className="text-neutral-500" />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  type="button"
                  onClick={() => handleClick(p)}
                  disabled={isBusy}
                  data-testid={`plan-${p.key}-cta`}
                  className="w-full inline-flex items-center justify-center gap-2 py-4 font-semibold text-sm rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={
                    p.ctaVariant === "outline"
                      ? {
                          border: `1.5px solid ${p.accent}`,
                          color: p.accent,
                          background: `${p.accent}12`,
                        }
                      : {
                          background: p.accent,
                          color: p.accent === "#D4AF37" ? "#000" : "#fff",
                          boxShadow: `0 20px 60px -20px ${p.accent}cc`,
                        }
                  }
                >
                  {isBusy ? (<><Loader2 size={14} className="animate-spin" />Redirecting…</>) : p.ctaLabel}
                </button>

                {/* Note */}
                {p.ctaNote && (
                  <div className="mt-3 text-center text-xs text-neutral-400 flex items-center justify-center gap-1.5">
                    {p.ctaNote.startsWith("Cancel") && <Lock size={10} />}
                    <span>{p.ctaNote}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* VALUE PROPS + ROADMAP */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 border border-white/5 bg-[#0a0a0a] p-6">
          <div className="lg:col-span-3 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUE_PROPS.map((v) => (
              <div key={v.title} className="flex items-start gap-3">
                <v.Icon size={22} className="text-amber-500 mt-0.5 shrink-0" strokeWidth={1.6} />
                <div>
                  <div className="text-xs font-semibold text-white mb-1">{v.title}</div>
                  <div className="text-[11px] text-neutral-400 whitespace-pre-line leading-relaxed">{v.body}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Roadmap teaser */}
          <div className="lg:col-span-2 border border-purple-500/40 bg-purple-500/5 p-5 rounded">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-purple-300">BeatCredsi Roadmap</div>
              <span className="text-[9px] font-mono-tech uppercase tracking-widest px-2 py-0.5 border border-purple-500/60 text-purple-300 rounded-full">
                Coming August 2026
              </span>
            </div>
            <div className="grid grid-cols-5 gap-2 text-center">
              {[
                { Icon: LineChart,       label: "Royalty\nDashboard (RD)" },
                { Icon: Sparkles,        label: "Legacy\nHunter (LH)" },
                { Icon: AlertTriangle,   label: "AI\nAlerts (AL)" },
                { Icon: LineChart,       label: "Forecast\nEngine (FE)" },
                { Icon: DollarSign,      label: "AI\nAccounting (AIA)" },
              ].map((s, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <s.Icon size={16} className="text-purple-300" strokeWidth={1.6} />
                  <div className="text-[9px] text-neutral-400 whitespace-pre-line leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center text-[10px] text-neutral-500">
              More tools. More power. More opportunities.
            </div>
          </div>
        </div>

        {/* Legal */}
        <p className="mt-10 text-center text-xs text-neutral-500 max-w-2xl mx-auto leading-relaxed">
          Every paid plan auto-renews monthly and can be cancelled anytime from
          your account.
          {" · "}
          <Link to="/refunds" className="text-amber-500 hover:underline">Refund policy</Link>
          {" · "}
          <Link to="/terms" className="text-amber-500 hover:underline">Terms</Link>
        </p>

        {/* GraduationCap import satisfier — keeps future-plan roadmap ready */}
        <span className="hidden"><GraduationCap size={1} /></span>
      </main>

      <Footer />
    </div>
  );
}
